<template>
	<div>
		<div class="row">
      <div class="col">
        <q-toggle v-model="settings.shared"
              :label="$t('label.shared')"
              @input="update"
        />
      </div>
      <div class="col">
        <q-spinner-cube v-if="updating" color="primary" size="2em"/>
      </div>
		</div>
		<div class="row" v-if="settings.shared">
			<q-input :value="dateInputValue"
					 :label="$t('label.sharedUntil')"
					 @click="() => $refs.qDateProxy.show()"
					 filled
					 dense
			>
				<template v-slot:append>
					<q-icon name="event" class="cursor-pointer">
						<q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
							<q-date :value="datePickerValue"
									:options="sharedUntilDates"
									@input="datePickerInput"
									minimal
							/>
						</q-popup-proxy>
					</q-icon>
				</template>
			</q-input>
		</div>
		<div class="row">
			<q-toggle v-model="settings.simplified"
					  :label="$t('label.simplified')"
					  @input="update"
			/>
		</div>
		<div class="row">
			<q-toggle v-model="settings.showBundleImages"
					  :label="$t('label.showBundleImages')"
					  @input="update"
			/>
		</div>
		<q-separator spaced/>
		<div class="row">
			<div class="col">
				<q-btn @click="updateClipboard(permalink)"
					   :label="$t('button.copyWatchlistLink')"
					   color="primary"
					   :disable="updating || !settings.shared"
				/>
			</div>
      <div class="col">
        <q-btn @click="updateClipboard(neutralPermalink)"
               :label="$t('button.copyNeutralWatchlistLink')"
               color="secondary"
               :disable="updating || !settings.shared"
        />
      </div>
		</div>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import {UPDATE_WATCHLIST, SET_SNACK} from "@/store/actions.type";
	import {DOMAINS, SNACK_LEVEL, ZOHO_LANGUAGE_ISO} from "@/common/constants";
	import {defaultShareValidityDays} from "@/common/config";
	import moment from "moment";

	export default {
		name: "WatchlistShare",
		data() {
			return {
				settings: {
					shared: this.$store.state.watchlist.watchlist.shared,
					sharedUntil: null,
					simplified: this.$store.state.watchlist.watchlist.simplified,
					showBundleImages: this.$store.state.watchlist.watchlist.showBundleImages
				},
				updating: false,
				today: moment().format('YYYY/MM/DD')
			}
		},
		computed: {
			...mapGetters(['watchlist']),
			datePickerValue() {
				return moment(this.settings.sharedUntil).format('YYYY/MM/DD');
			},
			dateInputValue() {
				return moment(this.settings.sharedUntil).format('DD-MM-YYYY');
			},
			permalink() {
				let locale = this.$i18n.locale;

				if(this.watchlist.zohoLanguage) {
					locale = ZOHO_LANGUAGE_ISO[this.watchlist.zohoLanguage];
				}

				return DOMAINS[0][locale] + this.$router.resolve({name: 'sharedWatchlist', params: {hash: this.watchlist.permalinkHash}}).href;
			},
      neutralPermalink() {
        let locale = this.$i18n.locale;

        if(this.watchlist.zohoLanguage) {
          locale = ZOHO_LANGUAGE_ISO[this.watchlist.zohoLanguage];
        }

        return DOMAINS[1][locale] + this.$router.resolve({name: 'sharedWatchlist', params: {hash: this.watchlist.permalinkHash}}).href;
      }
		},
		mounted() {
			if (this.watchlist.shared && this.watchlist.sharedUntil) {
				this.settings.sharedUntil = this.watchlist.sharedUntil * 1000;
			} else {
				this.settings.sharedUntil = this.getDefaultShareUntilDate();
			}

			if(this.settings.sharedUntil < moment()) {
				this.settings.shared = false;
				this.settings.sharedUntil = this.getDefaultShareUntilDate();
			}
		},
		methods: {
			datePickerInput(value, reason, details) {
				this.settings.sharedUntil = moment(value, 'YYYY/MM/DD').valueOf();
				this.$refs.qDateProxy.hide();
				this.update();
			},
			sharedUntilDates(date) {
				return date > this.today;
			},
			getDefaultShareUntilDate() {
				return moment().add(defaultShareValidityDays, 'days').valueOf();
			},
			async update() {
				this.updating = true;

				// reset to default sharedUntil date
				if (!this.settings.shared) {
					this.settings.sharedUntil = this.getDefaultShareUntilDate();
				}

				await this.$store.dispatch(UPDATE_WATCHLIST, {
					object: this.watchlist, props: {...this.settings, sharedUntil: this.settings.shared ? this.settings.sharedUntil / 1000 : 0}
				});
				this.updating = false;

				if (this.settings.shared) {
					this.updateClipboard(this.permalink);
				}
			},
			updateClipboard(newClip) {
				// todo using clipboard fallback until permissions api leaves working draft status and gains support
				this.updateClipboardFallback(newClip);
				return;

				if (!navigator.clipboard) {
					this.updateClipboardFallback(newClip);
				} else {
					// throws fatal TypeError in Firefox
					navigator.permissions.query({name: "clipboard-write"}).then(result => {
						if (result.state == "granted" || result.state == "prompt") {
							navigator.clipboard.writeText(newClip).then(() => {
								// success
								this.$store.dispatch(SET_SNACK, 'watchlistLinkSuccessfullyCopied');
							}, () => {
								// failed to use clipboard api, fallback to execCommand
								this.updateClipboardFallback(newClip);
							})
						}
					});
				}
			},
			updateClipboardFallback(newClip) {
				const txtArea = document.createElement('textarea');
				txtArea.value = newClip;
				txtArea.style.position = "fixed";
				txtArea.style.top = -4000;
				txtArea.style.left = -4000;
				document.body.appendChild(txtArea);
				txtArea.focus();
				txtArea.select();

				try {
					if (document.execCommand('copy')) {
						this.$store.dispatch(SET_SNACK, 'watchlistLinkSuccessfullyCopied');
					} else {
						this.$store.dispatch(SET_SNACK, {snack: 'watchlistLinkCopyError', level: SNACK_LEVEL.ERROR});
					}
				} catch (err) {
					this.$store.dispatch(SET_SNACK, {snack: 'watchlistLinkCopyError', level: SNACK_LEVEL.ERROR});
				}

				document.body.removeChild(txtArea);
			}
		}
	}
</script>

<style scoped>

</style>